import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BarChart2,
  Users,
  Layout,
  Check,
  Calendar,
  CloudSun,
  CloudRain,
  Settings,
  UserPlus,
  LogOut,
  User,
} from "lucide-react";
import { wellnessService } from "@/services/wellnessService";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState, useEffect } from "react";
import { TeamInviteButton } from "@/components/team/TeamInviteButton";
import { useUser } from "@/contexts/UserContext";
import { getUserStats, getStreakMessage, getWeeklyCheckInsMessage, UserStats } from "@/services/userStatsService";
import NotificationDrawer from "@/components/notifications/NotificationDrawer";

// Demo context removed - no longer needed

type DashboardHeaderProps = {
  activeTab: string;
  setActiveTab: (value: string) => void;
  userName: string;
};

// Positive messages for different weather conditions
const weatherMessages = {
  Clouds: "Perfect day to focus on your goals while staying cozy!",
  Clear: "A bright day full of possibilities!",
  Rain: "A refreshing day to recharge and reflect!",
  Snow: "A magical day to embrace new challenges!",
  Thunderstorm: "An energizing atmosphere to spark your creativity!",
  Drizzle: "A gentle reminder to take things one step at a time!",
  Mist: "A mysterious day perfect for deep thinking!",
  Fog: "A day of calm focus and clarity within!",
  default: "Today is full of opportunities for growth!",
};

const quotes = [
  "The only way to do great work is to love what you do.",
  "Believe you can and you're halfway there.",
  "Your attitude determines your direction.",
  "Success is not final, failure is not fatal: it is the courage to continue that counts.",
  "The future belongs to those who believe in the beauty of their dreams.",
  "Every challenge is an opportunity in disguise.",
  "Progress, not perfection, is what we're striving for.",
  "Small steps every day lead to big results over time.",
  "Your potential is endless. Go do what you were created to do.",
];

const DashboardHeader = ({
  activeTab,
  setActiveTab,
  userName,
}: DashboardHeaderProps) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { logout } = useAuth();

  const [quote, setQuote] = useState("");
  const [weather, setWeather] = useState<{
    temp?: number;
    condition?: string;
    message?: string;
    icon?: string;
  }>({});
  const [loading, setLoading] = useState(true);
  const [userStats, setUserStats] = useState<UserStats | null>(null);

  const firstName =
    userName && userName !== "User" ? userName.split(" ")[0] : "there";

  const currentTeamId = user?.teamId;
  const currentTeamName = user?.teamName;
  const isSoloUser = user?.persona === "independentSolo" || user?.persona === "organizationalSolo";

  console.log("currentTeamId", currentTeamId);
  console.log("currentTeamName", currentTeamName);

  // Load user stats
  useEffect(() => {
    const loadUserStats = async () => {
      if (user?.id) {
        try {
          const stats = await getUserStats();
          setUserStats(stats);
        } catch (error) {
          console.error('Error loading user stats:', error);
        }
      }
    };

    loadUserStats();
  }, [user?.id]);

  useEffect(() => {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setQuote(randomQuote);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Get weather data from backend API
            // apiService returns data directly, not { success, data }
            const weatherData = await wellnessService.getWeather(latitude, longitude).catch(() => null);
            
            if (weatherData) {
              const mainCondition = weatherData.condition || "Clear";
              const positiveMessage =
                weatherMessages[
                  mainCondition as keyof typeof weatherMessages
                ] || weatherMessages.default;

              setWeather({
                temp: weatherData.temp,
                condition: weatherData.condition,
                message: positiveMessage,
                icon: weatherData.icon,
              });
            } else {
              setWeather({
                temp: 22,
                condition: "Clear",
                message: weatherMessages.default,
                icon: "01d",
              });
            }
          } catch (error) {
            console.error("Error fetching weather:", error);
            // Set default weather on error
            setWeather({
              temp: 22,
              condition: "Clear",
              message: weatherMessages.default,
              icon: "01d",
            });
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
          setWeather({
            temp: 22,
            condition: "Clear",
            message: weatherMessages.default,
            icon: "01d",
          });
          setLoading(false);
        }
      );
    } else {
      setWeather({
        temp: 22,
        condition: "Clear",
        message: weatherMessages.default,
        icon: "01d",
      });
      setLoading(false);
    }
  }, []);

  const getWeatherIcon = () => {
    const condition = weather.condition?.toLowerCase() || "";

    if (condition.includes("rain") || condition.includes("drizzle")) {
      return <CloudRain className="h-5 w-5 text-blue-500" />;
    }

    return <CloudSun className="h-5 w-5 text-blue-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Top section with welcome message and actions */}
      <div className="bg-gradient-to-r from-primary/10 to-background rounded-lg p-4 md:p-6 shadow-sm border">
        <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-start md:space-y-0">
          <div className="flex flex-col md:flex-row md:items-center md:gap-6">
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Welcome back, {firstName}! 👋
              </h1>
              <p className="text-muted-foreground text-sm md:text-base mt-1">
                {new Date().toLocaleDateString(undefined, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })} • Ready to make today count?
              </p>
            </div>

            {/* Quick stats badges */}
            <div className="flex md:hidden flex-wrap gap-2 mt-3">
              <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-200 text-xs">
                <Check className="w-3 h-3 mr-1" />
                {getStreakMessage(userStats?.currentStreak || 0)}
              </Badge>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs">
                <Calendar className="w-3 h-3 mr-1" />
                {getWeeklyCheckInsMessage(userStats?.checkInsThisWeek || 0)}
              </Badge>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-200">
                <Check className="w-3 h-3 mr-1" />
                {getStreakMessage(userStats?.currentStreak || 0)}
              </Badge>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 hover:bg-blue-200">
                <Calendar className="w-3 h-3 mr-1" />
                {getWeeklyCheckInsMessage(userStats?.checkInsThisWeek || 0)}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full h-10 w-10 bg-white/80 hover:bg-white transition-all shadow-sm"
                >
                  {getWeatherIcon()}
                </Button>
              </PopoverTrigger>
                    <PopoverContent className="w-[calc(100vw-2rem)] md:w-80">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <div className="font-medium">Today's Weather</div>
                          <div className="text-sm text-muted-foreground">
                            {new Date().toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 py-2 bg-gradient-to-r from-primary/5 to-background/0 p-3 rounded-md">
                          {loading ? (
                            <div className="animate-pulse h-12 w-12 rounded-full bg-blue-100"></div>
                          ) : (
                            <>
                              <div className="p-2 rounded-full bg-primary/10">
                                {getWeatherIcon()}
                              </div>
                              <div>
                                <div className="text-xl font-semibold">
                                  {weather.temp}°C
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {weather.condition}
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        <div className="pt-2 border-t">
                          <p className="text-sm font-medium">
                            {weather.message}
                          </p>
                          <p className="text-sm italic mt-2 text-muted-foreground">
                            "{quote}"
                          </p>
                        </div>
                      </div>
                    </PopoverContent>
            </Popover>

            {/* Quick action buttons */}
            <Button 
              onClick={() => navigate("/check-in")}
              className="bg-primary hover:bg-primary/90 text-white shadow-md h-11 text-sm md:text-base"
              size="default"
            >
              <Check className="w-4 h-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">My Daily Check-in</span>
              <span className="sm:hidden">Check-in</span>
            </Button>
            
            {/* Team Invite Button */}
            {currentTeamId && !isSoloUser && (
              <TeamInviteButton
                teamId={currentTeamId}
                teamName={currentTeamName}
                variant="outline"
                size="default"
                className="h-11 text-sm md:text-base"
              />
            )}

            {/* Notification Bell */}
            <NotificationDrawer />


            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10 ring-2 ring-primary/20 hover:ring-primary/40 transition-all">
                    <AvatarImage src={user?.avatarUrl} alt={user?.name || 'User'} />
                    <AvatarFallback>
                      <User className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.name || 'User'}</p>
                    <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => {
                  // Trigger the ProfileSettingsWidget to open
                  const settingsButton = document.querySelector('[data-testid="profile-settings-trigger"]') as HTMLButtonElement;
                  if (settingsButton) {
                    settingsButton.click();
                  } else {
                    // Fallback: navigate to settings page
                    navigate('/settings');
                  }
                }}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => logout()}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Navigation and settings section */}
      <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-center md:space-y-0">
        <Tabs value={activeTab} className="md:mr-4">
          <TabsList className="bg-card/40 p-1 w-full md:w-auto">
            <TabsTrigger
              value="overview"
              onClick={() => setActiveTab("overview")}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 md:flex-none h-10"
            >
              <Layout className="h-4 w-4 mr-1 md:mr-2" />
              <span className="text-sm md:text-base">Overview</span>
            </TabsTrigger>
            <TabsTrigger
              value="insights"
              onClick={() => setActiveTab("insights")}
              className="data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 md:flex-none h-10"
            >
              <BarChart2 className="h-4 w-4 mr-1 md:mr-2" />
              <span className="text-sm md:text-base">Insights</span>
            </TabsTrigger>
            {!isSoloUser && (
              <TabsTrigger
                value="team"
                onClick={() => setActiveTab("team")}
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm flex-1 md:flex-none h-10"
              >
                <Users className="h-4 w-4 mr-1 md:mr-2" />
                <span className="text-sm md:text-base">Team</span>
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

      </div>
    </div>
  );
};

export default DashboardHeader;
